import styled from '@emotion/styled';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { tokens } from '@hotjar/hotsauce';

import { useImage } from '../SingleHighlight/use-image';
import { ClickStats } from './AvatarsOnHeatmap/ClickStats';
import { viewBox } from './utils';
import type { Box, PositionedBox } from './utils';

const StyledClickStats = styled(ClickStats)`
  opacity: 1;
`;

const Wrapper = styled.div`
  display: block;
  position: relative;
`;

// the margin offset around the highlighted area
// It's static for now, but it's better to be dynamically changed
// based on the image/selected dimensions
const SELECTED_AREA_MARGIN: Box = {
  w: 100,
  h: 100,
};

export interface HeatmapImageProps {
  className?: string;
  clicksCount?: number;
  clicksPercentage?: number;
  isDesktop?: boolean;
  selectedArea: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  url: string;
  strokeWidth?: number;
  onReady?: (svg: SVGSVGElement | null) => void;
}

export interface HeatmapImageRef {
  get: () => SVGSVGElement | null;
}

export const HeatmapImage = forwardRef<HeatmapImageRef, HeatmapImageProps>(
  (
    {
      className,
      url,
      clicksCount,
      clicksPercentage,
      isDesktop,
      selectedArea,
      strokeWidth = 2,
      onReady,
    },
    ref,
  ) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const svgBoxRef = useRef<SVGRectElement>(null);

    const [svgSize, setSvgSize] = useState<PositionedBox>({ x: 0, y: 0, w: 0, h: 0 });
    const [svgBoxSize, setSvgBoxSize] = useState<PositionedBox>({ x: 0, y: 0, w: 0, h: 0 });

    useImperativeHandle(ref, () => ({
      get() {
        return svgRef.current;
      },
    }));

    const { encodedImage, size } = useImage(url);
    const [height, width] = size;

    useEffect(() => {
      if (onReady && svgRef.current) {
        onReady(svgRef.current ?? null);
      }
    });

    useEffect(() => {
      if (!width || !height) {
        return;
      }

      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setSvgSize({
          x: rect.x,
          y: rect.y,
          h: rect.height,
          w: rect.width,
        });
      }

      if (svgBoxRef.current) {
        const rect = svgBoxRef.current.getBoundingClientRect();
        setSvgBoxSize({
          x: rect.x,
          y: rect.y,
          h: rect.height,
          w: 0,
        });
      }
    }, [width, height, selectedArea, svgRef, svgBoxRef]);

    const isVisible = height > 0 && width > 0;

    if (!isVisible) {
      return null;
    }

    const highlightArea: PositionedBox = {
      x: selectedArea.x1,
      y: selectedArea.y1,
      w: selectedArea.x2 - selectedArea.x1,
      h: selectedArea.y2 - selectedArea.y1,
    };

    const statsPosition = {
      top: svgBoxSize.y - svgSize.y - strokeWidth,
      bottom: svgBoxSize.y - svgSize.y + svgBoxSize.h - strokeWidth,
      left: svgBoxSize.x - svgSize.x - strokeWidth,
    };

    return (
      <Wrapper className={className}>
        <StyledClickStats
          containerHeight={svgSize.h}
          containerWidth={svgSize.w}
          clicksCount={clicksCount ?? 0}
          clicksPercentage={clicksPercentage ?? 0}
          y1={statsPosition.top}
          y2={statsPosition.bottom}
          left={statsPosition.left}
          isDesktop={isDesktop ?? true}
        />
        <svg
          ref={svgRef}
          viewBox={viewBox(highlightArea, SELECTED_AREA_MARGIN)}
          height="100%"
          width="100%"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin meet"
        >
          <image href={encodedImage} width={width} height={height} />
          <rect
            ref={svgBoxRef}
            x={highlightArea.x}
            y={highlightArea.y}
            width={highlightArea.w}
            height={highlightArea.h}
            fill="none"
            stroke={tokens.color.white}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </Wrapper>
    );
  },
);
